import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ChannelCategoryService } from '@service/channel/channel-category.service';
import { ChannelCategory, getExistingRowIds, MAX_CHANNEL_CATEGORIES } from 'icreate/common/models/channel-category';
import { ChannelGroup } from 'icreate/common/models/channel-group';
import {
    DialogChannelCategoryComponent,
    DialogType,
} from '../dialog-channel-category/dialog-channel-category.component';
import { Subject, takeUntil, take, lastValueFrom } from 'rxjs';
import { ChannelGroupService } from '@service/channel/channel-group.service';
import { MatDialog } from '@angular/material/dialog';
import { ChannelSheetService } from '@service/channel/channel-sheet.service';
import { TranslateService } from '@ngx-translate/core';
import { VesselService } from '@service/vessel.service';
import { DialogChannelGroupComponent } from '../dialog-channel-group/dialog-channel-group.component';
import { getValidationErrors, mapErrors } from 'icreate/common/validators';
import { categoryDisplayNameMaxLen, CategoryValidator } from '@models/models-validators/category-validator';
import { systemChannelGroupNum } from 'icreate/common/models/system-channels';
import { generateNextAvailableNum } from 'icreate/common/models/channel';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { MoreOptionsComponent } from '../more-options/more-options.component';
import { DeleteConfirmationDialogComponent } from '@components/delete-confirmation-dialog/delete-confirmation-dialog.component';

@Component({
    selector: 'app-channel-panel-category',
    templateUrl: './channel-panel-category.component.html',
    styleUrls: ['./channel-panel-category.component.scss'],
    standalone: false,
})
export class ChannelPanelCategoryComponent implements OnInit, OnDestroy {
    readonly CATEGORY_ADD = DialogType.add;
    readonly CATEGORY_EDIT = DialogType.edit;
    readonly CATEGORY_DELETE = DialogType.delete;

    @Input() selectedChannel: any;
    @Input() selectedChannelId: string;
    @Input() canEditSheet: boolean;

    channelCategoryList: ChannelCategory[] = [];
    channelGroupList: ChannelGroup[] = [];
    selectedCategory = new ChannelCategory();
    overlayRef: OverlayRef;

    private destroySubscriptionSubject$ = new Subject<void>();

    constructor(
        private channelCategoryService: ChannelCategoryService,
        private channelGroupService: ChannelGroupService,
        private channelSheetService: ChannelSheetService,
        private vesselService: VesselService,
        private translateService: TranslateService,
        private dialog: MatDialog,
        private overlay: Overlay
    ) {}

    ngOnInit() {
        this.channelCategoryService.list$
            .pipe(takeUntil(this.destroySubscriptionSubject$))
            .subscribe(channelCategoryList => {
                this.channelCategoryList = channelCategoryList;
            });

        this.channelGroupService.list$.pipe(takeUntil(this.destroySubscriptionSubject$)).subscribe(channelGroupList => {
            this.channelGroupList = channelGroupList;
        });
    }

    ngOnDestroy() {
        this.destroySubscriptionSubject$.next();
        this.destroySubscriptionSubject$.complete();
    }

    getGroupsCount(categoryId) {
        return this.channelGroupList.filter(group => group.categoryId == categoryId).length;
    }

    openMore(event: MouseEvent, category: ChannelCategory) {
        event.stopImmediatePropagation();

        this.selectedCategory = category;

        this.overlayRef?.dispose();

        const positionStrategy = this.overlay
            .position()
            .flexibleConnectedTo(event.target as HTMLElement)
            .withPositions([
                {
                    originX: 'end',
                    originY: 'bottom',
                    overlayX: 'end',
                    overlayY: 'top',
                },
            ]);

        this.overlayRef = this.overlay.create({
            positionStrategy,
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-transparent-backdrop',
        });

        const portal = new ComponentPortal(MoreOptionsComponent);
        const componentRef = this.overlayRef.attach(portal);
        componentRef.instance.disableOptions = !this.canEditSheet;
        componentRef.instance.disableDelete = this.isSystemChannelCategory();

        //plus 1 for system channel category
        componentRef.instance.disableDuplicate =
            this.channelCategoryList.length > MAX_CHANNEL_CATEGORIES || this.isSystemChannelCategory();

        componentRef.instance.onDeleteEmitter.subscribe(() => this.onDelete());
        componentRef.instance.onEditEmitter.subscribe(() => this.onEdit());
        componentRef.instance.onDuplicateEmitter.subscribe(() => this.onDuplicate());

        this.overlayRef
            .backdropClick()
            .pipe(take(1))
            .subscribe(() => this.closeMore());
    }

    onEdit() {
        const dialogRef = this.dialog.open(DialogChannelCategoryComponent, {
            width: '474px',
            data: {
                title: 'channel.edit-category.title',
                type: DialogType.edit,
                selectedChannelCategory: this.selectedCategory,
            },
        });
        const subscribeDialog = dialogRef.componentInstance.onEditCategory.subscribe(
            async (channelCategory: ChannelCategory) => {
                this.channelSheetService.showLoading();
                try {
                    const vesselId = this.vesselService.getActiveVesselId();
                    await this.channelCategoryService.update(channelCategory, vesselId);
                    await this.channelGroupService.getList(vesselId);
                } catch (err) {
                    console.error(err);
                } finally {
                    this.channelSheetService.hideLoading();
                }
            }
        );
        dialogRef.afterClosed().subscribe(() => {
            subscribeDialog.unsubscribe();
        });

        this.closeMore();
    }

    async onDuplicate() {
        if (this.isSystemChannelCategory()) return;
        const copiedCategory = { ...this.selectedCategory };
        copiedCategory.id = '';
        const newCategoryName = this.getDuplicateCategoryName(copiedCategory.category, this.channelCategoryList);

        copiedCategory.category = newCategoryName;

        const dialogRef = this.dialog.open(DialogChannelCategoryComponent, {
            width: '474px',
            data: {
                title: 'channel.add-category',
                type: DialogType.add,
                selectedChannelCategory: copiedCategory,
                isDuplicate: true,
            },
        });

        const subscribeDialog = dialogRef.componentInstance.onAddCategory.subscribe(
            async (channelCategory: ChannelCategory) => {
                try {
                    this.channelSheetService.showLoading();
                    await this.channelCategoryService.add(channelCategory, channelCategory.vesselId);
                } catch (err) {
                    console.error(err);
                } finally {
                    this.channelSheetService.hideLoading();
                    this.closeMore();
                }
            }
        );
        dialogRef.afterClosed().subscribe(() => {
            subscribeDialog.unsubscribe();
        });

        this.closeMore();
    }

    async onDelete() {
        if (this.isSystemChannelCategory()) return;
        const dialogRef = this.dialog.open(DialogChannelCategoryComponent, {
            width: '474px',
            data: {
                title: this.getTranslate('channel.delete-category.title'),
                description: 'channel.delete-category.content',
                type: DialogType.delete,
                selectedChannelCategory: this.selectedCategory,
            },
        });

        const categoryToDelete: ChannelCategory = await lastValueFrom(dialogRef.afterClosed());

        if (!categoryToDelete) {
            this.closeMore();
            return;
        }

        const advancedDialogRef = this.dialog.open(DeleteConfirmationDialogComponent, {
            width: '474px',
            data: {
                title: 'channel.more-option.category.delete.title',
                name: this.selectedCategory.category,
                message1: 'channel.more-option.category.delete.message1',
                message2: 'channel.more-option.category.delete.message2',
                cancelButton: 'channel.more-option.category.delete.button.cancel',
                buttonDelete: 'channel.more-option.category.delete.button.delete',
                inputLegend: 'channel.more-option.category.delete.button.legend',
            },
        });

        const advancedConfirmed = await lastValueFrom(advancedDialogRef.afterClosed());
        if (!advancedConfirmed) {
            this.closeMore();
            return;
        }

        try {
            this.channelSheetService.showLoading();
            const vesselId = this.vesselService.getActiveVesselId();
            await this.channelCategoryService.delete(this.selectedCategory.id, vesselId);
            await this.channelGroupService.getList(vesselId);
            this.channelSheetService.setActiveGroup(this.channelGroupList[0]);
        } catch (err) {
            console.error(err);
        } finally {
            this.channelSheetService.hideLoading();
            this.closeMore();
        }
    }

    getTranslate(text) {
        return this.translateService.instant(text, { category: this.selectedCategory.category });
    }

    closeMore = () => {
        this.overlayRef?.dispose();
        this.overlayRef = null;
        this.selectedCategory = null;
    };

    getDuplicateCategoryName(categoryName: string, categoryList: ChannelCategory[]) {
        let uniqueName = categoryName;
        let counter = 1;

        while (categoryList.some(category => category.category === uniqueName)) {
            uniqueName = `${categoryName}_copy_${counter}`;
            counter++;
        }

        return uniqueName;
    }

    isSystemChannelCategory() {
        if (!this.selectedCategory) return false;
        return this.selectedCategory.rowId == systemChannelGroupNum;
    }
}
